import styled from 'styled-components';

import { ButtonProps } from './Button.types';

const ButtonBase = styled.button<ButtonProps>`
  // Layout
  display: inline-flex;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fontFamily.inter}, Arial, Helvetica, sans-serif !important; // otherwise overridden by Brandon Font on deployed environments

  height: ${({ height, size, theme }) => {
    switch (size) {
      case 'medium':
        return theme.spacing['5Xl'];
      case 'small':
        return theme.spacing['4Xl'];
      case 'xs':
        return theme.spacing['3Xl'];
      default:
        return height || 'auto';
    }
  }};

  width: ${({ fullwidth }) => (fullwidth ? '100%' : 'fit-content')};
  padding: ${({ size }) => {
    switch (size) {
      case 'medium':
      case 'small':
        return '12px 24px';
      case 'xs':
        return '8px';
      default:
        return '14px 32px';
    }
  }};

  justify-content: center;
  align-items: center;

  cursor: pointer;
  border: none;
  outline: none;

  //Typography
  ${({ size, theme }) => {
    switch (size) {
      case 'medium':
        return theme.label.md;

      case 'small':
        return theme.label.sm;
      case 'xs':
        return theme.label.xs;
      default:
        return theme.label.lg;
    }
  }}

  border-radius: ${({ theme }) => theme.borderRadius.round};

  // States
  &:disabled {
    background: ${({ theme }) => theme.background.disabled};
    color: ${({ theme }) => theme.text.disabled};
    cursor: default;
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=5696%3A28896&t=CtAlx07GkXkNwTP8-4 Reference}
 */
export const PrimaryButton = styled(ButtonBase)<ButtonProps>`
  background: ${({ theme }) => theme.background.primary};
  color: ${({ theme }) => theme.reference.other.white};

  &:not(:disabled):active {
    background: ${({ theme }) => theme.background.primaryActive};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      background: ${({ theme }) => theme.background.primaryHover};
    }
  }

  &:focus-visible {
    ${({ theme }) => theme.focusBoxShadow(theme.shadow.focusVisible)}
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=5694%3A28063&t=zPeMPvwgx57URlEt-4 Reference}
 */
export const PrimaryInverseButton = styled(ButtonBase)<ButtonProps>`
  background: ${({ theme }) => theme.background.default};
  color: ${({ theme }) => theme.text.primary};

  &:not(:disabled):active {
    background: ${({ theme }) => theme.background.primaryActive};
    color: ${({ theme }) => theme.text.inverse};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      background: ${({ theme }) => theme.background.defaultHover};
      color: ${({ theme }) => theme.text.primaryHover};
    }
  }

  &:focus-visible {
    ${({ theme }) => theme.focusBoxShadow(theme.shadow.focusVisible)}
  }
`;

/**
 * {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=5696%3A29155&t=zPeMPvwgx57URlEt-4 Reference}
 */
export const PrimaryDestructiveButton = styled(ButtonBase)<ButtonProps>`
  background: ${({ theme }) => theme.background.critical};
  color: ${({ theme }) => theme.text.inverse};

  &:not(:disabled):active {
    background: ${({ theme }) => theme.background.criticalActive};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      background: ${({ theme }) => theme.background.criticalHover};
    }
  }

  &:focus-visible {
    ${({ theme }) => theme.focusBoxShadow(theme.shadow.focusVisible)}
  }
`;

/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=5694%3A28337&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const SecondaryButton = styled(ButtonBase)<ButtonProps>`
  background: transparent;
  color: ${({ theme }) => theme.text.strong};
  ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.strong)}

  &:disabled {
    color: ${({ theme }) => theme.text.disabled};
    background: transparent;
    ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.disabled)};
  }

  &:not(:disabled):active {
    color: ${({ theme }) => theme.text.inverse};
    background: ${({ theme }) => theme.background.primaryActive};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.text.inverse};
      background: ${({ theme }) => theme.background.primaryHover};

      // Do not show button border when hovering.
      // If focused, then show the focus border.
      &:not(:focus-visible) {
        box-shadow: none;
      }
    }
  }

  &:focus-visible {
    // Need to join inner aligned borders for the border and the focus shadow
    box-shadow: ${({ theme }) =>
      theme.innerAlignBorder(theme.border.sm, theme.border.strong, true, true) +
      ',' +
      theme.focusBoxShadow(theme.shadow.focusVisible, false, true)};
  }
`;

/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=5694%3A28347&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const SecondaryInverseButton = styled(SecondaryButton)<ButtonProps>`
  color: ${({ theme }) => theme.text.inverse};

  ${({ theme }) => theme.innerAlignBorder(theme.border.sm, theme.border.inverse)}

  &:focus-visible {
    // Need to join inner aligned borders for the border and the focus shadow
    box-shadow: ${({ theme }) =>
      theme.innerAlignBorder(theme.border.sm, theme.border.inverse, true, true) +
      ',' +
      theme.focusBoxShadow(theme.shadow.focusVisible, false, true)};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.text.primaryHover};
      background: ${({ theme }) => theme.background.defaultHover};
    }
  }

  &:disabled {
    color: ${({ theme }) => theme.text.disabledSubtle};
  }
`;

/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=5681%3A28455&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const TertiaryButton = styled(SecondaryButton)<ButtonProps>`
  background: transparent;
  border: none;
  box-shadow: none;
  color: ${({ theme }) => theme.text.primary};

  &:disabled {
    box-shadow: none;
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.text.primaryHover};

      background: ${({ theme }) => theme.background.defaultHover};
    }
  }
`;

/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6775%3A25252&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const TertiaryInverseButton = styled(TertiaryButton)<ButtonProps>`
  color: ${({ theme }) => theme.text.inverse};
  box-shadow: none;
  &:disabled {
    color: ${({ theme }) => theme.text.disabledSubtle};
    box-shadow: none;
  }
`;
/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6775%3A25409&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const TextButton = styled<any>(ButtonBase)<ButtonProps>`
  padding: 0px;
  height: auto;
  color: ${({ theme }) => theme.text.primary};
  background: transparent;
  text-decoration: underline;
  text-underline-offset: ${({ theme }) => theme.spacing.sm};

  &:disabled {
    color: ${({ theme }) => theme.text.disabled};
    background: transparent;
  }

  &:not(:disabled):active {
    color: ${({ theme }) => theme.text.primaryActive};
  }
  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.text.primaryHover};
    }
  }
`;

/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6775%3A25419&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const TextInverseButton = styled(TextButton)<ButtonProps>`
  color: ${({ theme }) => theme.text.inverse};

  &:disabled {
    color: ${({ theme }) => theme.text.disabledSubtle};
    background: transparent;
  }

  &:not(:disabled):active {
    color: ${({ theme }) => theme.text.inverseActive};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.text.inverseHover};
    }
  }
`;

/** {@linkplain https://www.figma.com/file/gSZXqLIiOfDf2FD2O8TzjZ/Components?node-id=6783%3A25324&t=uLnAnVWW7FgDWtZv-4 Reference} */
export const TextDestructiveButton = styled(TextButton)<ButtonProps>`
  color: ${({ theme }) => theme.text.critical};

  &:disabled {
    color: ${({ theme }) => theme.text.disabled};
  }

  &:not(:disabled):active {
    color: ${({ theme }) => theme.text.criticalActive};
  }

  &:not(:disabled):not(:active) {
    &:hover,
    &.hover {
      color: ${({ theme }) => theme.text.criticalHover};
    }
  }
`;
